import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Button,
  Chip,
  CircularProgress,
  InputBase,
  Box,
  Select,
  MenuItem,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useProjectContext } from "../../context/ProjectContext";
import useStyles from "./styles";
import {
  Actor,
  CustomPhotoAvatar,
  SelectedAvatarLabActor,
  AvatarGroup,
  AvatarGroupItem,
  ListAvatarGroupsV3Response,
} from "../../context/types";
import CheckMark from "./components/CheckMark";
import { Search } from "@mui/icons-material";
import { styled } from "@mui/system";
import { THEME_COLORS } from "../../context/constants";
import {
  deleteActor,
  getCustomPhotoAvatarsByWorkspace,
  getCustomPhotoAvatarsByOrganization,
  checkPhotoAvatarStatus,
  createAvatarFromUrl,
  createCustomPhotoAvatarV3FromImage,
  createCustomPhotoAvatarV3FromPrompt,
  createCustomPhotoAvatarV3FromVideo,
  updateCustomPhotoAvatarV3LookName,
  listCustomPhotoAvatarV3Groups,
  getCustomPhotoAvatarV3Group,
  syncCustomPhotoAvatarV3Look,
  getAvatarGroupsByOrganization,
  getAvatarGroupsByWorkspace,
  createAvatarGroup,
  deleteCustomPhotoAvatar,
  pollAvatarGroupTrainingStatus,
  deleteAvatarGroup,
  addMotionToAvatar,
  addSoundEffectToAvatar,
  getCustomPhotoAvatarById,
} from "../../client/api";
import { useParams } from "react-router-dom";
import { useUserContext } from "../../context/UserContext";
import { buildAvatarPrompt } from "../../utils/buildAvatarPrompt";
import { canUseFeatures } from "../../utils/utils";
import LockIcon from "./components/LockIcon";
import AvatarCreationModal from "./components/AvatarCreationModal";
import { useNotification } from "../../context/NotificationContext";
import { StyledInputLabel } from "../../theme/StyledInputLabel";
import ThreeDotMenu from "./components/ThreeDotMenu";
import AvatarGroupCard from "./components/AvatarGroupCard";
import AvatarGroupCreationModal from "./components/AvatarGroupCreationModal";
import UnifiedAvatarCard from "./components/UnifiedAvatarCard";
import ChooseLookModal from "./components/ChooseLookModal";
import LookCreationModal from "./components/LookCreationModal";
import AddMotionModal from "./components/AddMotionModal";
import { MotionType } from "../../context/types";

interface ActorsDialogProps {
  open: boolean;
  actors?: Actor[];
  avatarLabActors?: CustomPhotoAvatar[];
  onClose: () => void;
  onSelectActors: (selectedActors: Actor[]) => void;
  onSelectAvatarLabActors?: (
    selectedAvatarLabActors: CustomPhotoAvatar[]
  ) => void;
  selectedActors?: Actor[];
  availableForTemplates?: boolean;
  mode?: "normal" | "template"; // Add mode prop
}

const SearchBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f1f1f1",
  border: `1px solid ${THEME_COLORS.gray}`,
  padding: "2px 10px",
  borderRadius: "14px",
  marginBottom: "10px",
  maxWidth: "300px",
}));

const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "40px",
    }}
  >
    <CircularProgress />
  </div>
);

const ActorsDialog: React.FC<ActorsDialogProps> = ({
  open,
  actors = [],
  avatarLabActors = [],
  onClose,
  onSelectActors,
  onSelectAvatarLabActors,
  selectedActors: initialSelectedActors = [],
  availableForTemplates = false,
  mode = "normal", // Default to normal mode
}) => {
  const classes = useStyles();
  const { avatars, talkingPhotos, fetchAvatars, cachedCustomPhotoAvatars, setCachedCustomPhotoAvatars, cachedAvatarGroups, setCachedAvatarGroups } = useProjectContext();
  const { currentWorkspace, user } = useUserContext();
  const { videoPageId } = useParams();
  const [isLoadingCustomAvatars, setIsLoadingCustomAvatars] = useState(false);

  // In template mode, start with empty selection; in normal mode, use initialSelectedActors
  const [selectedActors, setSelectedActors] = useState<Actor[]>(
    mode === "template" ? [] : initialSelectedActors
  );
  const [selectedAvatarLabActors, setSelectedAvatarLabActors] =
    useState<CustomPhotoAvatar[]>(avatarLabActors);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hoveredActorId, setHoveredActorId] = useState<string | null>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { addNotification } = useNotification();
  const currentRequestRef = useRef<string | null>(null);

  const actorsPerPage = 10;

  // New states for Avatar Lab
  const [activeTab, setActiveTab] = useState<
    "avatars" | "talkingPhotos" | "avatarLab"
  >("avatars");
  const [canUseCustomActors, setCanUseCustomActors] = useState(
    canUseFeatures(currentWorkspace)
  );
  // Initialize from cached state if available
  const [customPhotoAvatars, setCustomPhotoAvatars] = useState<
    CustomPhotoAvatar[]
  >(cachedCustomPhotoAvatars || []);
  const [isAvatarCreationOpen, setIsAvatarCreationOpen] = useState(false);
  const [isAvatarLabInfoOpen, setIsAvatarLabInfoOpen] = useState(false);
  const [isCreatingAvatar, setIsCreatingAvatar] = useState(false);
  // Polling state lives in a ref, NOT React state. Tracking timer handles in
  // useState caused a stale-closure trap: every sync update called
  // setCustomPhotoAvatars which re-ran this effect; the cleanup fired the
  // cancel function (setting cancelled=true on the live tick closure) but the
  // intervals map still had the pollKey entry, so the effect body skipped
  // starting a fresh poller. Result: V3 polling halted after exactly one
  // cycle, and any avatar that took >5s on HeyGen stayed in 'processing'
  // forever.
  const pollersRef = useRef<Map<string, { cancel: () => void }>>(new Map());

  // Avatar Groups states - initialize from cached state if available
  const [avatarGroups, setAvatarGroups] = useState<AvatarGroup[]>(cachedAvatarGroups || []);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [pollingGroups, setPollingGroups] = useState<Set<string>>(new Set());
  const [groupCreationModalOpen, setGroupCreationModalOpen] = useState(false);
  const [selectedAvatarForGroup, setSelectedAvatarForGroup] =
    useState<CustomPhotoAvatar | null>(null);
  const [lookCreationModalOpen, setLookCreationModalOpen] = useState(false);
  const [chooseLookModalOpen, setChooseLookModalOpen] = useState(false);
  const [selectedGroupForLook, setSelectedGroupForLook] =
    useState<AvatarGroup | null>(null);
  const [isCreatingLook, setIsCreatingLook] = useState(false);

  // Motion and sound effect states
  const [addMotionModalOpen, setAddMotionModalOpen] = useState(false);
  const [selectedAvatarForMotion, setSelectedAvatarForMotion] =
    useState<CustomPhotoAvatar | null>(null);
  const [isAddingMotion, setIsAddingMotion] = useState(false);
  const [isAddingSoundEffect, setIsAddingSoundEffect] = useState(false);

  // Loading states for initial data fetching
  const [loadingStates, setLoadingStates] = useState({
    avatars: false,
    customPhotoAvatars: false,
  });

  // New state for organization/workspace filter
  const [avatarSourceFilter, setAvatarSourceFilter] = useState<
    "organization" | "workspace"
  >("organization");

  const fetchAllData = async () => {
    if (!currentWorkspace || !user) return;

    // Only show loading if we don't have cached data
    const shouldShowLoading = !avatars || !cachedCustomPhotoAvatars;

    try {
      if (shouldShowLoading) {
        setLoadingStates((prev) => ({
          ...prev,
          avatars: true,
          customPhotoAvatars: true,
        }));
      }

      await Promise.all([
        fetchAvatarsData(),
        fetchCustomPhotoAvatars(),
        fetchAvatarGroups(),
      ]);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      addNotification("Error loading data", "error");
    } finally {
      if (shouldShowLoading) {
        setLoadingStates((prev) => ({
          ...prev,
          avatars: false,
          customPhotoAvatars: false,
        }));
      }
    }
  };

  // Fetch all necessary data when dialog opens
  useEffect(() => {
    if (open && currentWorkspace) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentWorkspace, avatarSourceFilter]);

  // Handle selection reset based on mode
  useEffect(() => {
    if (open) {
      // Reset selection in template mode each time dialog opens
      if (mode === "template") {
        setSelectedActors([]);
        setSelectedAvatarLabActors([]);
      } else {
        setSelectedActors(initialSelectedActors);
      }
    }
  }, [open, mode, initialSelectedActors]);

  useEffect(() => {
    if (!open || !currentWorkspace) return;

    // Find all groups that need polling
    const groupsNeedingPolling = avatarGroups.filter(
      (group) =>
        group.training_status === "training" ||
        group.training_status === "pending"
    );

    // Update polling groups set
    setPollingGroups(new Set(groupsNeedingPolling.map((g) => g._id)));

    if (groupsNeedingPolling.length === 0) return;

    const interval = setInterval(async () => {
      try {
        await fetchAvatarGroups();

        // Check if any groups are still training after refresh
        const updatedGroups = await getAvatarGroupsByWorkspace(
          currentWorkspace._id
        );
        const stillTraining = updatedGroups.filter(
          (group: any) =>
            group.training_status === "training" ||
            group.training_status === "pending"
        );

        if (stillTraining.length === 0) {
          clearInterval(interval);
        }
      } catch (error) {
        console.error("Error auto-refreshing training groups:", error);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [avatarGroups, open, currentWorkspace]);

  // Poll for groups that are "ready" but don't have valid looks data yet
  // This can happen when the group training finishes but avatar data isn't populated yet
  useEffect(() => {
    if (!open || !currentWorkspace) return;

    // Helper to check if a group has valid looks with images (inline to avoid dependency issues)
    const hasValidLooks = (group: AvatarGroup): boolean => {
      if (!group.looks || group.looks.length === 0) return false;
      return group.looks.some((look) => {
        if (typeof look === "object" && look !== null) {
          return look.image_url_list && look.image_url_list.length > 0;
        }
        return false;
      });
    };

    // Find groups that are ready but don't have valid looks
    const groupsNeedingData = avatarGroups.filter(
      (group) => group.training_status === "ready" && !hasValidLooks(group)
    );

    if (groupsNeedingData.length === 0) return;

    // Poll every 2 minutes for groups missing data
    const interval = setInterval(async () => {
      try {
        await fetchAvatarGroups();
      } catch (error) {
        console.error("Error refreshing groups for missing data:", error);
      }
    }, 120000); // 2 minutes

    // Also do an immediate refresh
    fetchAvatarGroups();

    return () => clearInterval(interval);
  }, [avatarGroups, open, currentWorkspace]);

  const handleRefreshGroup = async (groupId: string) => {
    try {
      await fetchAvatarGroups(); // This will refresh all groups
      addNotification("Group status refreshed", "info");
    } catch (error) {
      console.error("Error refreshing group:", error);
      addNotification("Error refreshing group status", "error");
    }
  };

  const handleOpenGroupLookCreation = (group: AvatarGroup) => {
    setSelectedGroupForLook(group);
    setLookCreationModalOpen(true);
  };

  const handleOpenChooseLookModal = (group: AvatarGroup) => {
    setSelectedGroupForLook(group);
    setChooseLookModalOpen(true);
  };

  const handleCreateLook = async (
    formData: any,
    type: "generate" | "upload",
    imageFile?: File
  ) => {
    if (!selectedGroupForLook || !currentWorkspace || !user) return;

    try {
      setIsCreatingLook(true);

      if (type === "generate") {
        // V3 prompt-to-avatar attached to existing group (reference images allowed since avatarGroupId is set)
        await createCustomPhotoAvatarV3FromPrompt({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          name: formData.name,
          prompt: buildAvatarPrompt(formData) || formData.prompt,
          avatarGroupId: selectedGroupForLook.group_id,
          ...(formData.referenceImages && { referenceImages: formData.referenceImages }),
        });
      } else {
        // V3 image upload attached to existing group
        await createCustomPhotoAvatarV3FromImage({
          workspaceId: currentWorkspace._id,
          userEmail: user.email,
          name: formData.name,
          avatarGroupId: selectedGroupForLook.group_id,
          image: imageFile!,
        });
      }

      addNotification(
        `Look "${formData.name}" creation started successfully`,
        "success"
      );
      setLookCreationModalOpen(false);

      // Refresh data to show the new look
      await fetchAvatarGroups();
      await fetchCustomPhotoAvatars();
    } catch (error) {
      console.error("Error creating look:", error);
      addNotification("Error creating look, please try again.", "error");
    } finally {
      setIsCreatingLook(false);
    }
  };

  const fetchAvatarsData = async () => {
    try {
      await fetchAvatars(
        user?.organization?._id || "",
        currentWorkspace?._id || ""
      );
      setCanUseCustomActors(canUseFeatures(currentWorkspace));
    } catch (error) {
      console.error("Error fetching avatars:", error);
      addNotification("Error fetching avatars", "error");
    }
  };

  const fetchCustomPhotoAvatars = async () => {
    if (!currentWorkspace || !user) return;
    try {
      setIsLoadingCustomAvatars(true);

      const requestId = `${avatarSourceFilter}-${Date.now()}`;
      currentRequestRef.current = requestId;

      let avatars;

      if (avatarSourceFilter === "organization" && user.organization) {
        avatars = await getCustomPhotoAvatarsByOrganization(
          user.organization._id
        );
      } else {
        avatars = await getCustomPhotoAvatarsByWorkspace(currentWorkspace._id);
      }

      if (currentRequestRef.current === requestId) {
        const sortedAvatars = avatars.sort(
          (a: CustomPhotoAvatar, b: CustomPhotoAvatar) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setCustomPhotoAvatars(sortedAvatars);
        // Update global cache
        setCachedCustomPhotoAvatars(sortedAvatars);
      }
    } catch (error) {
      console.error("Error fetching custom photo avatars:", error);
      addNotification("Error fetching avatars", "error");
    } finally {
      setIsLoadingCustomAvatars(false);
    }
  };

  const fetchAvatarGroups = async () => {
    if (!currentWorkspace || !user) return;

    try {
      let groups;
      if (avatarSourceFilter === "organization" && user.organization) {
        groups = await getAvatarGroupsByOrganization(user.organization._id);
      } else {
        groups = await getAvatarGroupsByWorkspace(currentWorkspace._id);
      }
      setAvatarGroups(groups);
      // Update global cache
      setCachedAvatarGroups(groups);
    } catch (error) {
      console.error("Error fetching avatar groups:", error);
      addNotification("Error fetching avatar groups", "error");
    }
  };

  // Fetch V3 avatar groups (existing identities) for the "attach to existing
  // identity" picker, scoped to the current organization. Returns the full list
  // across pagination tokens, capped to a reasonable size so the picker stays
  // responsive.
  const fetchV3AvatarGroups = async (
    organizationId: string,
    maxItems: number = 100
  ) => {
    const all: AvatarGroupItem[] = [];
    let token: string | undefined = undefined;
    try {
      do {
        const res: ListAvatarGroupsV3Response = await listCustomPhotoAvatarV3Groups({
          organizationId,
          limit: 50,
          token,
        });
        all.push(...res.data);
        token = res.has_more ? res.next_token : undefined;
      } while (token && all.length < maxItems);
      return all.slice(0, maxItems);
    } catch (error) {
      console.error("Error listing V3 avatar groups:", error);
      addNotification("Error loading existing identities", "error");
      return [];
    }
  };

  // Set up status polling for processing avatars (handles both V2 and V3).
  // Pollers are tracked in pollersRef, NOT in React state, so sync updates that
  // call setCustomPhotoAvatars don't cancel the in-flight poller via cleanup.
  // This effect only manages start/stop transitions: spin up a poller when a
  // new processing avatar appears, cancel one when its avatar leaves the
  // processing set.
  useEffect(() => {
    if (!customPhotoAvatars) return;

    const processingAvatars = customPhotoAvatars.filter(
      (avatar) => avatar.status === "processing"
    );

    const activeKeys = new Set<string>();

    processingAvatars.forEach((avatar) => {
      const pollKey = avatar.apiVersion === "v3" ? avatar.group_id : avatar.generation_id;
      if (!pollKey) return;
      activeKeys.add(pollKey);
      if (pollersRef.current.has(pollKey)) return;

      if (avatar.apiVersion === "v3") {
        // V3: backoff strategy — 5s for first 30s, then 15s, hard cap at 10 min.
        const startTime = Date.now();
        const MAX_DURATION = 10 * 60 * 1000;
        let cancelled = false;
        let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

        const tick = async () => {
          if (cancelled) return;
          const reachedTerminal = await checkAvatarStatusV3(avatar);
          if (cancelled) return;
          if (reachedTerminal || Date.now() - startTime >= MAX_DURATION) {
            pollersRef.current.delete(pollKey);
            return;
          }
          const elapsed = Date.now() - startTime;
          const delay = elapsed > 30_000 ? 15_000 : 5_000;
          timeoutHandle = setTimeout(tick, delay);
        };

        timeoutHandle = setTimeout(tick, 5_000);
        pollersRef.current.set(pollKey, {
          cancel: () => {
            cancelled = true;
            if (timeoutHandle) clearTimeout(timeoutHandle);
          },
        });
      } else {
        // V2: legacy 20s setInterval polling
        const interval = setInterval(() => {
          checkAvatarStatus(avatar.generation_id!);
        }, 20000);
        pollersRef.current.set(pollKey, {
          cancel: () => clearInterval(interval),
        });
      }
    });

    // Cancel pollers for avatars that are no longer in the processing set
    // (e.g., status flipped to success/failed, or the avatar was removed).
    for (const [pollKey, poller] of Array.from(pollersRef.current.entries())) {
      if (!activeKeys.has(pollKey)) {
        poller.cancel();
        pollersRef.current.delete(pollKey);
      }
    }
    // NOTE: no cleanup return here on purpose — cleanup must NOT fire on every
    // re-run, otherwise sync-triggered setCustomPhotoAvatars updates would
    // cancel the very poller that just triggered them. The unmount-only
    // cleanup lives in the dedicated effect below.
  }, [customPhotoAvatars]);

  // Cancel all pollers on unmount. Empty deps so this never re-runs.
  useEffect(() => {
    return () => {
      Array.from(pollersRef.current.values()).forEach((poller) => poller.cancel());
      pollersRef.current.clear();
    };
  }, []);

  const checkAvatarStatus = async (generationId: string) => {
    try {
      const response = await checkPhotoAvatarStatus(generationId);
      if (response.avatar) {
        const previousAvatar = customPhotoAvatars.find(
          (a) => a.generation_id === generationId
        );
        const wasProcessing = previousAvatar?.status === "processing";
        const isNowSuccess = response.avatar.status === "success";

        // Update the avatar in state
        setCustomPhotoAvatars((prev) =>
          prev.map((avatar) =>
            avatar.generation_id === generationId ? response.avatar : avatar
          )
        );

        // Also update the cached avatars
        setCachedCustomPhotoAvatars((prev: CustomPhotoAvatar[] | null) =>
          prev
            ? prev.map((avatar: CustomPhotoAvatar) =>
                avatar.generation_id === generationId ? response.avatar : avatar
              )
            : null
        );

        // If avatar just completed processing, refresh groups
        // (backend may have created a group for it automatically)
        if (wasProcessing && isNowSuccess) {
          await fetchAvatarGroups();
        }
      }
    } catch (error) {
      console.error("Error checking avatar status:", error);
    }
  };

  // V3 polling: hits the per-LOOK sync endpoint, which calls HeyGen's
  // GET /v3/avatars/looks/{look_id} AND writes the result back to the local
  // CustomPhotoAvatar. Polling the GROUP was wrong here — when a new look is
  // attached to an already-trained group, the group reports 'completed'
  // immediately while the look is still 'processing', causing the polling
  // loop to terminate against the group's status without ever updating the
  // local DB record (which then re-appeared as processing on next refetch).
  //
  // Backend normalizes HeyGen 'completed' -> 'success' on write, so the UI's
  // existing status === 'success' checks light up automatically.
  const TERMINAL_V3_STATES = new Set(["success", "completed", "failed", "pending_consent"]);
  const checkAvatarStatusV3 = async (avatar: CustomPhotoAvatar): Promise<boolean> => {
    // Fall back to group polling only if the record predates the look_id field
    // (older V3 records may not have one). New records always have avatar_look_id.
    if (!avatar.avatar_look_id) {
      if (!avatar.group_id) return true;
      try {
        const response = await getCustomPhotoAvatarV3Group(avatar.group_id);
        const groupStatus = response.data.status;
        return TERMINAL_V3_STATES.has(groupStatus);
      } catch (error) {
        console.error("Error checking V3 avatar group status:", error);
        return false;
      }
    }

    try {
      const { avatar: updatedAvatar } = await syncCustomPhotoAvatarV3Look(avatar.avatar_look_id);

      setCustomPhotoAvatars((prev) =>
        prev.map((a) => (a._id === avatar._id ? { ...a, ...updatedAvatar } : a))
      );
      setCachedCustomPhotoAvatars((prev: CustomPhotoAvatar[] | null) =>
        prev
          ? prev.map((a: CustomPhotoAvatar) =>
              a._id === avatar._id ? { ...a, ...updatedAvatar } : a
            )
          : null
      );

      const isTerminal = TERMINAL_V3_STATES.has(updatedAvatar.status);
      if (isTerminal) {
        // Refresh groups so the newly-completed look shows up inside its
        // populated group.looks array (the create endpoint pushes the ref;
        // this just makes sure the UI sees the latest snapshot).
        await fetchAvatarGroups();
      }
      return isTerminal;
    } catch (error) {
      console.error("Error syncing V3 avatar look status:", error);
      return false;
    }
  };

  // Rename a V3 avatar look. HeyGen rejects this for 'prompt' look types —
  // callers should hide the rename UI for prompt-type avatars.
  const handleRenameV3Look = async (avatar: CustomPhotoAvatar, newName: string) => {
    if (!avatar.avatar_look_id) {
      addNotification("This avatar can't be renamed (missing look ID)", "warning");
      return;
    }
    try {
      const response = await updateCustomPhotoAvatarV3LookName(
        avatar.avatar_look_id,
        newName
      );
      if (response.avatar) {
        updateAvatarInState(avatar._id, response.avatar);
      }
      addNotification("Avatar renamed", "success");
    } catch (error: any) {
      console.error("Error renaming avatar look:", error);
      addNotification(
        error?.response?.data?.message ||
          "Failed to rename avatar (prompt-type avatars can't be renamed)",
        "error"
      );
    }
  };

  const isAvatarLabActor = (actor: Actor) => {
    if (!actor.preview_image_url) return false;
    return customPhotoAvatars.some(
      (avatar) =>
        !!avatar.generation_id && actor.preview_image_url.includes(avatar.generation_id)
    );
  };

  const getAvatarLabData = (actor: Actor) => {
    if (!actor.preview_image_url) return null;
    return customPhotoAvatars.find(
      (avatar) =>
        !!avatar.generation_id && actor.preview_image_url.includes(avatar.generation_id)
    );
  };

  const handleCreateAvatar = async (
    formData: any,
    type: "generate" | "upload" | "library" | "video",
    imageFile?: File,
    assetUrl?: string,
    videoFile?: File
  ) => {
    if (!currentWorkspace || !user) return;

    try {
      setIsCreatingAvatar(true);

      // V3 is now the default flow for image/prompt/video.
      // The legacy "library" path (createAvatarFromUrl) still uses V2 since V3
      // doesn't expose a URL endpoint — for that we keep the old endpoint.
      if (type === "generate") {
        // V3 prompt-to-avatar
        await createCustomPhotoAvatarV3FromPrompt({
          workspaceId: currentWorkspace._id,
          videoPageId: videoPageId,
          userEmail: user.email,
          name: formData.name,
          prompt: buildAvatarPrompt(formData) || formData.prompt,
          // Reference images are only allowed when avatarGroupId is set; backend rejects otherwise
          ...(formData.avatarGroupId && {
            avatarGroupId: formData.avatarGroupId,
            referenceImages: formData.referenceImages,
          }),
        });
      } else if (type === "upload") {
        // V3 image upload
        await createCustomPhotoAvatarV3FromImage({
          workspaceId: currentWorkspace._id,
          videoPageId: videoPageId,
          userEmail: user.email,
          name: formData.name,
          avatarGroupId: formData.avatarGroupId,
          image: imageFile!,
        });
      } else if (type === "video") {
        // V3 digital twin from video
        await createCustomPhotoAvatarV3FromVideo({
          workspaceId: currentWorkspace._id,
          videoPageId: videoPageId,
          userEmail: user.email,
          name: formData.name,
          avatarGroupId: formData.avatarGroupId,
          video: videoFile!,
        });
      } else if (type === "library" && assetUrl) {
        // Legacy V2 path — V3 has no URL endpoint
        await createAvatarFromUrl({
          workspaceId: currentWorkspace._id,
          videoPageId: videoPageId,
          userEmail: user.email,
          ...formData,
          imageUrl: assetUrl,
        });
      }

      await fetchCustomPhotoAvatars();
      // Also pull the freshly-created AvatarGroup so isAvatarGroupCreating can
      // resolve the local group immediately. Without this, the very first
      // render after create sees an unresolved group → the loading overlay
      // shows correctly, but the avatar→group link isn't visible to any other
      // code paths until the first poll cycle's fetchAvatarGroups runs.
      await fetchAvatarGroups();
      setIsAvatarCreationOpen(false);
      addNotification(
        `Avatar ${type === "generate" ? "generated" : "created"} successfully`,
        "success"
      );
    } catch (error: any) {
      console.error(
        `Error ${
          type === "generate" ? "generating" : "creating"
        } custom photo avatar:`,
        error
      );
      addNotification(
        error?.response?.data?.message ||
          `Error ${
            type === "generate" ? "generating" : "creating"
          } avatar, please try again.`,
        "error"
      );
    } finally {
      setIsCreatingAvatar(false);
    }
  };

  const handleDeleteAvatar = async (avatarId: string) => {
    try {
      await deleteCustomPhotoAvatar(avatarId);
      addNotification("Avatar deleted successfully", "success");
      await fetchCustomPhotoAvatars();
      await fetchAvatarGroups();
    } catch (error) {
      console.error("Error deleting avatar:", error);
      addNotification("Error deleting avatar", "error");
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteAvatarGroup(groupId);
      await fetchAvatarGroups();
      addNotification("Avatar group deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting avatar group:", error);
      addNotification("Failed to delete avatar group", "error");
    }
  };

  const handleOpenGroupCreation = (avatar: CustomPhotoAvatar) => {
    setSelectedAvatarForGroup(avatar);
    setGroupCreationModalOpen(true);
  };

  const handleCloseGroupCreation = () => {
    setGroupCreationModalOpen(false);
    setSelectedAvatarForGroup(null);
  };

  const handleCreateGroup = async (avatarId: string, groupName: string) => {
    if (!currentWorkspace || !user) return;

    try {
      setIsCreatingGroup(true);

      // Create the group
      const response = await createAvatarGroup({
        workspaceId: currentWorkspace._id,
        userEmail: user.email,
        base_avatar_id: avatarId,
        name: groupName,
      });

      addNotification(
        "Avatar group created successfully. Training started...",
        "success"
      );

      // Start polling for training status
      const pollTrainingStatus = async () => {
        try {
          const updatedGroup = await pollAvatarGroupTrainingStatus(
            response.avatarGroupId
          );

          // Update the groups list with the trained group
          await fetchAvatarGroups();
          addNotification(
            `Avatar group "${groupName}" is now ready for look generation!`,
            "success"
          );
        } catch (error: any) {
          console.error("Error during group training:", error);
          addNotification(
            `Avatar group training failed: ${error.message}`,
            "error"
          );
          await fetchAvatarGroups(); // Refresh to show failed status
        }
      };

      // Start polling in background
      pollTrainingStatus();

      handleCloseGroupCreation();
    } catch (error) {
      console.error("Error creating avatar group:", error);
      addNotification("Error creating avatar group", "error");
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleToggleExpand = (groupId: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Motion and Sound Effect Handlers
  const handleOpenAddMotion = (avatar: CustomPhotoAvatar) => {
    setSelectedAvatarForMotion(avatar);
    setAddMotionModalOpen(true);
  };

  const handleCloseAddMotion = () => {
    setAddMotionModalOpen(false);
    setSelectedAvatarForMotion(null);
  };

  // Helper function to update avatar in all state locations
  const updateAvatarInState = (avatarId: string, updatedAvatar: CustomPhotoAvatar) => {
    // Update standalone avatars
    setCustomPhotoAvatars((prev) =>
      prev.map((avatar) =>
        avatar._id === avatarId ? updatedAvatar : avatar
      )
    );

    // Update cached standalone avatars
    setCachedCustomPhotoAvatars((prev: CustomPhotoAvatar[] | null) =>
      prev
        ? prev.map((avatar: CustomPhotoAvatar) =>
            avatar._id === avatarId ? updatedAvatar : avatar
          )
        : null
    );

    // Update looks within avatar groups
    setAvatarGroups((prev) =>
      prev.map((group) => ({
        ...group,
        looks: group.looks.map((look) =>
          typeof look === "object" && look._id === avatarId
            ? updatedAvatar
            : look
        ),
      }))
    );

    // Update cached avatar groups
    setCachedAvatarGroups((prev: AvatarGroup[] | null) =>
      prev
        ? prev.map((group: AvatarGroup) => ({
            ...group,
            looks: group.looks.map((look) =>
              typeof look === "object" && look._id === avatarId
                ? updatedAvatar
                : look
            ),
          }))
        : null
    );
  };

  // Poll for avatar details to get motion_preview_url after motion is added
  const pollForMotionPreview = async (avatarId: string, maxAttempts = 30) => {
    let attempts = 0;
    const pollInterval = 5000; // 5 seconds

    const checkForPreview = async (): Promise<void> => {
      attempts++;
      try {
        const updatedAvatar = await getCustomPhotoAvatarById(avatarId);

        // Update state with latest avatar data
        updateAvatarInState(avatarId, updatedAvatar);

        // If motion_preview_url is available, we're done
        if (updatedAvatar.motion_preview_url) {
          addNotification("Motion preview is now available!", "success");

          // Refresh avatar groups to update the look in the modal
          await fetchAvatarGroups();

          // Update selected group if modal is open
          if (selectedGroupForLook && currentWorkspace) {
            const updatedGroups = await getAvatarGroupsByWorkspace(currentWorkspace._id);
            const updatedGroup = updatedGroups.find(
              (g: AvatarGroup) => g._id === selectedGroupForLook._id
            );
            if (updatedGroup) {
              setSelectedGroupForLook(updatedGroup);
            }
          }
          return;
        }

        // If we haven't reached max attempts and still no preview, continue polling
        if (attempts < maxAttempts) {
          setTimeout(checkForPreview, pollInterval);
        }
      } catch (error) {
        console.error("Error polling for motion preview:", error);
        // Continue polling even on error
        if (attempts < maxAttempts) {
          setTimeout(checkForPreview, pollInterval);
        }
      }
    };

    // Start polling after a short delay
    setTimeout(checkForPreview, pollInterval);
  };

  const handleAddMotion = async (
    avatarId: string,
    motionType: MotionType,
    prompt?: string
  ) => {
    try {
      setIsAddingMotion(true);

      // Notify user that motion process has begun
      addNotification(
        "Motion process started. This may take a few minutes.",
        "info"
      );

      const response = await addMotionToAvatar(avatarId, {
        motion_type: motionType,
        prompt,
      });

      // Response now contains baseAvatar (with avatar_group set) and motionAvatar (new avatar with motion)
      // motionAvatar may be null if still processing

      // IMMEDIATELY update the base avatar in state with the group association
      // This triggers the loading state to show right away
      if (response.baseAvatar) {
        setCustomPhotoAvatars((prev) =>
          prev.map((avatar) =>
            avatar._id === avatarId ? response.baseAvatar : avatar
          )
        );
        setCachedCustomPhotoAvatars((prev: CustomPhotoAvatar[] | null) =>
          prev
            ? prev.map((avatar: CustomPhotoAvatar) =>
                avatar._id === avatarId ? response.baseAvatar : avatar
              )
            : null
        );
      }

      if (response.motionAvatar) {
        // Motion avatar was created - add it to state
        addNotification(
          `Motion avatar "${response.motionAvatar.name}" created successfully!`,
          "success"
        );

        // Start polling for motion preview URL if not yet available
        if (!response.motionAvatar.motion_preview_url) {
          pollForMotionPreview(response.motionAvatar._id);
        }
      } else {
        // Motion is still processing on HeyGen's side
        addNotification(
          "Motion is being generated. You can close this modal and come back later.",
          "info"
        );
      }

      handleCloseAddMotion();

      // Refresh avatar groups to get the new motion look in the group's looks array
      await fetchAvatarGroups();

      // Also update the selected group for look modal if it's open
      if (selectedGroupForLook) {
        const updatedGroups = await getAvatarGroupsByWorkspace(currentWorkspace!._id);
        const updatedGroup = updatedGroups.find(
          (g: AvatarGroup) => g._id === selectedGroupForLook._id
        );
        if (updatedGroup) {
          // If we have a motionAvatar from the response, ensure it's in the looks
          // The backend might not have added it to the group's looks yet
          const motionAvatar = response.motionAvatar;
          if (motionAvatar) {
            const motionAvatarExists = updatedGroup.looks?.some(
              (look: CustomPhotoAvatar) => look._id === motionAvatar._id
            );

            if (!motionAvatarExists) {
              // Add the motion avatar to the looks array
              setSelectedGroupForLook({
                ...updatedGroup,
                looks: [...(updatedGroup.looks || []), motionAvatar],
              });
            } else {
              // Motion avatar exists, merge its data to ensure is_motion and other fields are set
              const mergedLooks = updatedGroup.looks?.map((look: CustomPhotoAvatar) => {
                if (look._id === motionAvatar._id) {
                  return {
                    ...look,
                    ...motionAvatar,
                  };
                }
                return look;
              });
              setSelectedGroupForLook({
                ...updatedGroup,
                looks: mergedLooks,
              });
            }
          } else {
            setSelectedGroupForLook(updatedGroup);
          }
        }
      }
    } catch (error: any) {
      console.error("Error adding motion to avatar:", error);
      addNotification(
        error.response?.data?.message || "Failed to add motion to avatar",
        "error"
      );
    } finally {
      setIsAddingMotion(false);
    }
  };

  const handleAddSoundEffect = async (avatar: CustomPhotoAvatar) => {
    if (!avatar.is_motion) {
      addNotification(
        "Avatar must have motion added before adding sound effect",
        "error"
      );
      return;
    }

    try {
      setIsAddingSoundEffect(true);

      const response = await addSoundEffectToAvatar(avatar._id);

      // Update state with initial response using helper function
      updateAvatarInState(avatar._id, response.avatar);

      addNotification("Sound effect added successfully to avatar", "success");

      // Refresh avatar groups to update the look with sound effect
      await fetchAvatarGroups();

      // Also update the selected group for look modal if it's open
      // We need to merge the response.avatar data to ensure background_sound_effect is included
      if (selectedGroupForLook) {
        const updatedGroups = await getAvatarGroupsByWorkspace(currentWorkspace!._id);
        const updatedGroup = updatedGroups.find(
          (g: AvatarGroup) => g._id === selectedGroupForLook._id
        );
        if (updatedGroup) {
          // Merge the response avatar data into the looks to ensure background_sound_effect is present
          const mergedLooks = updatedGroup.looks?.map((look: CustomPhotoAvatar) => {
            if (look._id === avatar._id) {
              return {
                ...look,
                background_sound_effect: response.avatar.background_sound_effect,
              };
            }
            return look;
          });
          setSelectedGroupForLook({
            ...updatedGroup,
            looks: mergedLooks,
          });
        }
      }
    } catch (error: any) {
      console.error("Error adding sound effect to avatar:", error);
      addNotification(
        error.response?.data?.message || "Failed to add sound effect to avatar",
        "error"
      );
    } finally {
      setIsAddingSoundEffect(false);
    }
  };

  const handleSelect = async (actor: Actor) => {
    if (!currentWorkspace || (!videoPageId && mode === "normal")) return;
    const isCustomActor = actor.gender !== "female" && actor.gender !== "male";
    if (isCustomActor && !canUseCustomActors) return;

    const isSelected = selectedActors.some(
      (selected) => selected.avatar_id === actor.avatar_id
    );

    if (isSelected) {
      // In normal mode, delete the actor from backend; in template mode, just deselect
      if (mode === "normal") {
        try {
          await deleteActor(actor.avatar_id, currentWorkspace?._id, videoPageId!);
        } catch (e: any) {
          console.error("Actor not found when deleting");
        }
      }
      // Remove actor from selection
      setSelectedActors((prev) =>
        prev.filter((selected) => selected.avatar_id !== actor.avatar_id)
      );
    } else {
      // Add actor to selection
      setSelectedActors((prev) => [...prev, actor]);
    }
  };

  const handleSelectAvatarLabActor = (avatar: CustomPhotoAvatar) => {
    if (avatar.status !== "success") {
      addNotification(
        "Please wait for avatar generation to complete before selecting",
        "warning"
      );
      return;
    }

    // V3 looks generate videos via avatar_look_id and don't carry V2's
    // image_key_list / selected_image_key — preview_image_url is enough for
    // the UI thumbnail. Mirror the validation in VideoPage so any record the
    // generator can use is also selectable here.
    const hasUsableSource =
      !!avatar.avatar_look_id ||
      !!avatar.selected_image_key ||
      (avatar.image_key_list && avatar.image_key_list.length > 0);

    if (!hasUsableSource) {
      addNotification(
        "This avatar does not have any generated images yet",
        "warning"
      );
      return;
    }

    const isSelected = selectedAvatarLabActors.some(
      (selected) => selected._id === avatar._id
    );

    if (isSelected) {
      setSelectedAvatarLabActors((prev) =>
        prev.filter((selected) => selected._id !== avatar._id)
      );
    } else {
      setSelectedAvatarLabActors((prev) => [...prev, avatar]);
    }
  };

  // Helper to check if a group has valid looks with images
  const groupHasValidLooks = (group: AvatarGroup): boolean => {
    if (!group.looks || group.looks.length === 0) return false;

    // Check if at least one look has an image
    return group.looks.some((look) => {
      if (typeof look === "object" && look !== null) {
        return look.image_url_list && look.image_url_list.length > 0;
      }
      return false;
    });
  };

  // Filter out avatars that are part of groups (except base avatars)
  // But keep showing avatars whose group is not ready yet (still training or missing data)
  const getStandaloneAvatars = () => {
    return customPhotoAvatars.filter((avatar) => {
      // V3 avatars store HeyGen's group id in group_id; V2 avatars use avatar_group (mongo _id).
      // Match against both fields on the group record so a "new look" attached to an existing
      // ready group is rolled up into that group's card instead of rendering as a standalone
      // processing tile at the end of the list.
      const mongoGroupId = avatar.avatar_group;
      const heygenGroupId = avatar.group_id;
      if (!mongoGroupId && !heygenGroupId) return true;

      const group = avatarGroups.find(
        (g) =>
          (mongoGroupId && g._id === mongoGroupId) ||
          (heygenGroupId && g.group_id === heygenGroupId)
      );

      // If group doesn't exist yet or is still training, show the avatar with loading state
      if (!group || group.training_status !== "ready") {
        return true;
      }

      // Group is "ready" but doesn't have valid looks with images yet
      // Keep showing the avatar with loading state
      if (!groupHasValidLooks(group)) {
        return true;
      }

      // Group is fully ready with images, avatar will be shown under the group
      return false;
    });
  };

  // Helper to check if an avatar is in "group creating" state.
  // Hard guard first: if the avatar itself is success with an image, it is
  // ready by definition — group-creating is a transient state about the
  // AVATAR not being ready yet. Showing the overlay over a fully-rendered
  // avatar because of stale group bookkeeping (missing AvatarGroup record,
  // training_status not yet flipped, etc.) is wrong.
  const isAvatarGroupCreating = (avatar: CustomPhotoAvatar): boolean => {
    if (avatar.status === "success" && avatar.image_url_list?.length > 0) {
      return false;
    }

    const mongoGroupId = avatar.avatar_group;
    const heygenGroupId = avatar.group_id;
    if (!mongoGroupId && !heygenGroupId) return false;

    const group = avatarGroups.find(
      (g) =>
        (mongoGroupId && g._id === mongoGroupId) ||
        (heygenGroupId && g.group_id === heygenGroupId)
    );

    // Group doesn't exist yet
    if (!group) return true;

    // Group is still training
    if (group.training_status !== "ready") return true;

    // Group is "ready" but doesn't have valid looks with images yet
    if (!groupHasValidLooks(group)) return true;

    return false;
  };

  // Filter for Avatar Lab
  const filterCustomAvatars = (avatars: CustomPhotoAvatar[]) => {
    switch (activeFilter) {
      case "Male":
        return avatars.filter((avatar) => avatar.gender === "male");
      case "Female":
        return avatars.filter((avatar) => avatar.gender === "female");
      case "Processing":
        return avatars.filter((avatar) => avatar.status === "processing");
      case "Completed":
        return avatars.filter((avatar) => avatar.status === "success");
      case "Failed":
        return avatars.filter((avatar) => avatar.status === "failed");
      default:
        return avatars;
    }
  };

  const filteredCustomAvatars = filterCustomAvatars(
    getStandaloneAvatars()
  ).filter((avatar) =>
    avatar.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCurrentCustomAvatars = () => {
    return filteredCustomAvatars.slice(
      (currentPage - 1) * actorsPerPage,
      currentPage * actorsPerPage
    );
  };

  const currentCustomAvatars = getCurrentCustomAvatars();
  const totalCustomPages = Math.ceil(
    filteredCustomAvatars.length / actorsPerPage
  );

  // Handle source filter change
  const handleSourceFilterChange = (source: "organization" | "workspace") => {
    setAvatarSourceFilter(source);
    setCurrentPage(1);
  };

  const handleMouseEnter = (actorId: string) => {
    setHoveredActorId(actorId);
  };

  const handleMouseLeave = () => {
    setHoveredActorId(null);
  };

  const filterActors = (actors: Actor[]) => {
    if (activeTab === "talkingPhotos") {
      switch (activeFilter) {
        case "Avatar Lab":
          if (customPhotoAvatars.length === 0) {
            return [];
          }
          return actors.filter((actor) => {
            if (!actor.preview_image_url) return false;
            return customPhotoAvatars.some(
              (avatar) =>
                avatar.generation_id &&
                actor.preview_image_url.includes(avatar.generation_id)
            );
          });
        default:
          return actors;
      }
    }

    switch (activeFilter) {
      case "Custom":
        return actors.filter(
          (actor) => actor.gender !== "female" && actor.gender !== "male"
        );
      case "Male":
        return actors.filter((actor) => actor.gender === "male");
      case "Female":
        return actors.filter((actor) => actor.gender === "female");
      case "Free":
        return actors.filter(
          (actor) => actor.gender === "male" || actor.gender === "female"
        );
      default:
        return actors;
    }
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const filteredActors = filterActors(avatars || []).filter((actor) =>
    JSON.stringify(actor).toLowerCase().includes(searchQuery)
  );

  const getCurrentActors = () => {
    const sourceActors =
      activeTab === "avatars" ? avatars || [] : talkingPhotos || [];

    const filtered = filterActors(sourceActors).filter((actor) =>
      JSON.stringify(actor).toLowerCase().includes(searchQuery)
    );

    const merged = [
      ...selectedActors.filter(
        (actor) =>
          (activeTab === "avatars" && !actor.isTalkingPhoto) ||
          (activeTab === "talkingPhotos" && actor.isTalkingPhoto)
      ),
      ...filtered.filter(
        (actor) =>
          !selectedActors.some(
            (selected) => selected.avatar_id === actor.avatar_id
          )
      ),
    ];

    return merged.slice(
      (currentPage - 1) * actorsPerPage,
      currentPage * actorsPerPage
    );
  };

  const currentActors = getCurrentActors();
  const totalPages = Math.ceil(
    filterActors(activeTab === "avatars" ? avatars || [] : talkingPhotos || [])
      .length / actorsPerPage
  );

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleUnselectAll = () => {
    setSelectedActors([]);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handleNext = () => {
    onSelectActors(selectedActors);
    onSelectAvatarLabActors?.(selectedAvatarLabActors);
    onClose();
  };

  // Handle video playback based on hovered actor
  useEffect(() => {
    Object.keys(videoRefs.current).forEach((key) => {
      const videoEl = videoRefs.current[key];
      if (videoEl) {
        if (hoveredActorId === key) {
          videoEl
            .play()
            .catch((error) => console.error("Play failed: ", error));
        } else {
          videoEl.pause();
          videoEl.currentTime = 0;
        }
      }
    });
  }, [hoveredActorId]);

  const availableFilters = !canUseCustomActors
    ? ["All", "Free", "Male", "Female"]
    : ["All", "Custom", "Male", "Female"];

  const photoAvatarTabFilters = ["All", "Avatar Lab"];

  const avatarLabFilters = [
    "All",
    "Male",
    "Female",
    "Processing",
    "Completed",
    "Failed",
  ];

  const isInitialLoading =
    loadingStates.avatars || loadingStates.customPhotoAvatars;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className={classes.dialog}>
        <div className={classes.dialogTitle}>
          <DialogTitle className={classes.dialogTitle}>
            Select the actors you'd like to have for your ad
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onClose}
            className={classes.closeButton}
            sx={{ color: "#FFBF0091" }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent sx={{ padding: "12px 20px" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              setActiveTab(newValue);
              setCurrentPage(1);
              setActiveFilter("All");
            }}
            sx={{
              color: THEME_COLORS.yellow,
              "& .MuiTab-root": { color: "black" },
              "& .Mui-selected": { color: THEME_COLORS.yellow },
              mb: 2,
            }}
          >
            <Tab label="Avatars" value="avatars" />
            <Tab label="Photo Avatars" value="talkingPhotos" />
            {!availableForTemplates && (
              <Tab
                label={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <AddIcon fontSize="small" />
                    Avatar Lab
                  </div>
                }
                value="avatarLab"
              />
            )}
          </Tabs>

          {isInitialLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <SearchBar>
                <InputBase
                  placeholder={
                    activeTab === "avatarLab"
                      ? "Search custom avatars..."
                      : "Search actors..."
                  }
                  value={searchQuery}
                  onChange={handleSearchChange}
                  fullWidth
                />
                <IconButton>
                  <Search />
                </IconButton>
              </SearchBar>
              {/* Avatar Lab Tab */}
              {activeTab === "avatarLab" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                      flexWrap: "wrap",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <StyledInputLabel>Source</StyledInputLabel>
                        <Select
                          value={avatarSourceFilter}
                          label="Source"
                          onChange={(e) =>
                            handleSourceFilterChange(
                              e.target.value as "organization" | "workspace"
                            )
                          }
                        >
                          <MenuItem value="organization">Organization</MenuItem>
                          <MenuItem value="workspace">This Workspace</MenuItem>
                        </Select>
                      </FormControl>

                      <div className={classes.filters}>
                        {avatarLabFilters.map((filter) => (
                          <Chip
                            key={filter}
                            label={filter}
                            clickable
                            color={
                              activeFilter === filter ? "primary" : "default"
                            }
                            onClick={() => handleFilterChange(filter)}
                          />
                        ))}
                      </div>
                    </div>

                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setIsAvatarCreationOpen(true)}
                      disabled={!canUseCustomActors}
                    >
                      Create New Avatar
                    </Button>

                    <Box
                      onClick={() => setIsAvatarLabInfoOpen(true)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        color: THEME_COLORS.darkYellow,
                        ml: 2,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        What is Avatar Lab?
                      </Typography>
                    </Box>
                  </div>

                  <div
                    style={{
                      marginBottom: "8px",
                      fontSize: "14px",
                      color: THEME_COLORS.gray,
                    }}
                  >
                    Showing avatars from:{" "}
                    <strong>
                      {avatarSourceFilter === "organization"
                        ? `Entire Organization${
                            user?.organization?.name
                              ? ` (${user.organization.name})`
                              : ""
                          }`
                        : `This Workspace (${
                            currentWorkspace?.name || "Current"
                          })`}
                    </strong>
                  </div>

                  {/* Unified Avatar List - Groups First, Then Standalone */}
                  <Grid container spacing={2} className={classes.actorsGrid}>
                    {/* Avatar Groups - Only show groups that are fully ready with images */}
                    {avatarGroups
                      .filter((group) =>
                        group.training_status === "ready" && groupHasValidLooks(group)
                      )
                      .map((group) => {
                        const selectedLooks = selectedAvatarLabActors.filter(
                          (look) => look.avatar_group === group._id
                        );
                        const isGroupSelected = selectedLooks.length > 0;

                        // Note: we deliberately do NOT pass isGroupCreating when a new
                        // look is being added to an already-ready group. The group's
                        // existing looks are still selectable; a full-card overlay
                        // would block them. The "Look generation started" toast plus
                        // the per-look spinner inside the Choose Look modal already
                        // communicate the in-flight new look.
                        return (
                          <Grid item xs={6} sm={4} md={3} key={group._id}>
                            <UnifiedAvatarCard
                              item={group}
                              type="group"
                              isSelected={isGroupSelected}
                              canSelect={true}
                              selectedAvatarLabActors={selectedAvatarLabActors}
                              onSelectAvatarLabActor={handleSelectAvatarLabActor}
                              onOpenGroupLookCreation={
                                handleOpenGroupLookCreation
                              }
                              onOpenChooseLookModal={handleOpenChooseLookModal}
                              onRefreshGroup={handleRefreshGroup}
                              hoveredActorId={hoveredActorId}
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}
                              onDeleteAvatar={handleDeleteGroup}
                              classes={classes}
                            />
                          </Grid>
                        );
                      })}

                    {/* Standalone Avatars */}
                    {currentCustomAvatars.map((avatar: CustomPhotoAvatar) => {
                      const isSelected = selectedAvatarLabActors.some(
                        (selected) => selected._id === avatar._id
                      );
                      const groupCreating = isAvatarGroupCreating(avatar);
                      const canSelect: any =
                        avatar.status === "success" &&
                        !groupCreating &&
                        (avatar.avatar_look_id ||
                          avatar.selected_image_key ||
                          avatar.image_key_list.length > 0);
                      const canCreateGroup =
                        avatar.status === "success" && !avatar.avatar_group && !groupCreating;

                      return (
                        <Grid item xs={6} sm={4} md={3} key={avatar._id}>
                          <UnifiedAvatarCard
                            item={avatar}
                            type="avatar"
                            isSelected={isSelected}
                            canSelect={canSelect}
                            canCreateGroup={canCreateGroup}
                            isGroupCreating={groupCreating}
                            onSelectAvatarLabActor={handleSelectAvatarLabActor}
                            onOpenGroupCreation={handleOpenGroupCreation}
                            onDeleteAvatar={handleDeleteAvatar}
                            isCreatingGroup={isCreatingGroup}
                            hoveredActorId={hoveredActorId}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            onRefreshGroup={handleRefreshGroup}
                            onOpenGroupLookCreation={
                              handleOpenGroupLookCreation
                            }
                            onOpenChooseLookModal={handleOpenChooseLookModal}
                            classes={classes}
                            onOpenAddMotion={handleOpenAddMotion}
                            onAddSoundEffect={handleAddSoundEffect}
                            isAddingMotion={isAddingMotion}
                            isAddingSoundEffect={isAddingSoundEffect}
                            // V3: rename look — hidden for prompt-type avatars (HeyGen rejects)
                            onRenameLook={
                              avatar.apiVersion === "v3" &&
                              avatar.avatar_look_id &&
                              avatar.avatarType !== "prompt"
                                ? (newName: string) => handleRenameV3Look(avatar, newName)
                                : undefined
                            }
                          />
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Pagination */}
                  {filteredCustomAvatars.length > 0 && (
                    <div className={classes.paginationControls}>
                      <Button
                        variant="contained"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </Button>
                      <Select
                        value={currentPage}
                        onChange={(e) =>
                          handlePageChange(Number(e.target.value))
                        }
                        style={{ margin: "0 10px" }}
                      >
                        {Array.from(
                          { length: totalCustomPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <MenuItem key={page} value={page}>
                            Page {page}
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        variant="contained"
                        disabled={currentPage === totalCustomPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}

                  {avatarGroups.length === 0 &&
                    filteredCustomAvatars.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: THEME_COLORS.gray,
                        }}
                      >
                        {searchQuery
                          ? "No avatars or groups match your search."
                          : `No avatars found in ${
                              avatarSourceFilter === "organization"
                                ? "the organization"
                                : "this workspace"
                            }.`}
                      </div>
                    )}
                </>
              )}
              {/* Existing Avatars and Talking Photos Tabs */}
              {activeTab !== "avatarLab" && (
                <>
                  {activeTab === "avatars" && (
                    <div className={classes.filters}>
                      {availableFilters.map((filter) => (
                        <Chip
                          key={filter}
                          label={filter}
                          clickable
                          color={
                            activeFilter === filter ? "primary" : "default"
                          }
                          onClick={() => handleFilterChange(filter)}
                        />
                      ))}
                    </div>
                  )}
                  {activeTab === "talkingPhotos" && (
                    <div className={classes.filters}>
                      {photoAvatarTabFilters.map((filter) => (
                        <Chip
                          key={filter}
                          label={
                            filter === "Avatar Lab" && isLoadingCustomAvatars
                              ? "Loading Avatar Lab..."
                              : filter
                          }
                          clickable
                          color={
                            activeFilter === filter ? "primary" : "default"
                          }
                          onClick={() => handleFilterChange(filter)}
                          disabled={
                            filter === "Avatar Lab" &&
                            (isLoadingCustomAvatars ||
                              customPhotoAvatars.length === 0)
                          }
                          icon={
                            filter === "Avatar Lab" &&
                            isLoadingCustomAvatars ? (
                              <CircularProgress size={16} />
                            ) : undefined
                          }
                        />
                      ))}
                    </div>
                  )}
                  <Grid container spacing={2} className={classes.actorsGrid}>
                    {currentActors &&
                      currentActors.map((actor: Actor) => {
                        const isCustomActor =
                          activeTab === "avatars" &&
                          actor.gender !== "female" &&
                          actor.gender !== "male";
                        const isLocked = isCustomActor && !canUseCustomActors;
                        const isAvatarLab = isAvatarLabActor(actor);
                        const avatarLabData = getAvatarLabData(actor);

                        return (
                          <Grid item xs={6} sm={4} md={3} key={actor.avatar_id}>
                            <div
                              className={`${classes.actorCard} ${
                                selectedActors.some(
                                  (selected) =>
                                    selected.avatar_id === actor.avatar_id
                                )
                                  ? classes.selected
                                  : ""
                              } ${isLocked ? classes.locked : ""}`}
                              onClick={() => handleSelect(actor)}
                              onMouseEnter={() =>
                                handleMouseEnter(actor.avatar_id)
                              }
                              onMouseLeave={handleMouseLeave}
                              style={{
                                position: "relative",
                                cursor: isLocked ? "not-allowed" : "pointer",
                              }}
                            >
                              {isAvatarLab && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "8px",
                                    left: "8px",
                                    zIndex: 5,
                                    padding: "2px 6px",
                                    borderRadius: "8px",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    backgroundColor: "#9C27B0",
                                    color: "white",
                                  }}
                                >
                                  Avatar Lab
                                </div>
                              )}

                              {isLocked && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "rgba(0,0,0,0.5)",
                                    borderRadius: "10px",
                                    zIndex: 2,
                                  }}
                                >
                                  <LockIcon />
                                </div>
                              )}

                              <div
                                className={classes.actorInfo}
                                style={{ position: "absolute", zIndex: 3 }}
                              >
                                <span
                                  style={{
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {actor?.avatar_name?.length > 10
                                    ? actor?.avatar_name?.substring(0, 10) +
                                      "..."
                                    : actor?.avatar_name}
                                </span>
                                {activeTab === "avatars" && (
                                  <span>
                                    {isAvatarLab && avatarLabData
                                      ? avatarLabData.gender
                                      : actor?.gender}
                                  </span>
                                )}
                              </div>

                              {actor && (
                                <>
                                  <video
                                    ref={(el) =>
                                      (videoRefs.current[actor.avatar_id] = el)
                                    }
                                    src={actor.preview_video_url}
                                    className={classes.actorVideo}
                                    loop
                                    preload="auto"
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      zIndex: 4,
                                      opacity:
                                        hoveredActorId === actor.avatar_id
                                          ? 1
                                          : 0,
                                      transition: "opacity 0.3s ease-in-out",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <img
                                    src={actor.preview_image_url}
                                    alt={actor.avatar_name}
                                    className={classes.actorImage}
                                    style={{
                                      zIndex: 0,
                                      minWidth: "150px",
                                      minHeight: "150px",
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: THEME_COLORS.gray,
                                    }}
                                  />
                                  <CheckMark
                                    isVisible={selectedActors.some(
                                      (selected) =>
                                        selected.avatar_id === actor.avatar_id
                                    )}
                                  />
                                </>
                              )}
                            </div>
                          </Grid>
                        );
                      })}
                  </Grid>

                  <div className={classes.paginationControls}>
                    <Button
                      variant="contained"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Select
                      value={currentPage}
                      onChange={(e) => handlePageChange(Number(e.target.value))}
                      style={{ margin: "0 10px" }}
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <MenuItem key={page} value={page}>
                            Page {page}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    <Button
                      variant="contained"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>

        <div className={classes.dialogFooter}>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span>{selectedActors.length} actors selected</span>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleUnselectAll}
            >
              Unselect All
            </Button>
          </div>
          <Button variant="contained" color="primary" onClick={handleNext}>
            Next
          </Button>
        </div>
      </div>

      {/* Avatar Creation Modal */}
      <AvatarCreationModal
        open={isAvatarCreationOpen}
        onClose={() => setIsAvatarCreationOpen(false)}
        onSubmit={handleCreateAvatar}
        isLoading={isCreatingAvatar}
        // V3: lazy-load existing identities for the "attach to existing identity" picker.
        // Scoped to the current user's organization so members of other orgs aren't visible.
        // Reference-images uploader inside the modal must require an avatarGroupId selection.
        loadExistingIdentities={
          user?.organization?._id
            ? () => fetchV3AvatarGroups(user.organization._id)
            : undefined
        }
        onRenameLook={handleRenameV3Look}
      />

      {/* Avatar Group Creation Modal */}
      <AvatarGroupCreationModal
        open={groupCreationModalOpen}
        onClose={handleCloseGroupCreation}
        onSubmit={handleCreateGroup}
        avatarName={selectedAvatarForGroup?.name || ""}
        avatarId={selectedAvatarForGroup?._id || ""}
        isLoading={isCreatingGroup}
      />

      {/* Look Creation Modal */}
      {selectedGroupForLook && (
        <LookCreationModal
          open={lookCreationModalOpen}
          onClose={() => {
            setLookCreationModalOpen(false);
            setSelectedGroupForLook(null);
          }}
          onSubmit={handleCreateLook}
          isLoading={isCreatingLook}
          group={selectedGroupForLook!}
        />
      )}

      {/* Choose Look Modal */}
      {selectedGroupForLook && (
        <ChooseLookModal
          open={chooseLookModalOpen}
          onClose={() => {
            setChooseLookModalOpen(false);
            setSelectedGroupForLook(null);
          }}
          group={selectedGroupForLook!}
          selectedAvatarLabActors={selectedAvatarLabActors}
          onSelectAvatarLabActor={handleSelectAvatarLabActor}
          onOpenAddMotion={handleOpenAddMotion}
          onAddSoundEffect={handleAddSoundEffect}
          isAddingMotion={isAddingMotion}
          isAddingSoundEffect={isAddingSoundEffect}
        />
      )}

      {/* Add Motion Modal */}
      {selectedAvatarForMotion && (
        <AddMotionModal
          open={addMotionModalOpen}
          onClose={handleCloseAddMotion}
          onSubmit={handleAddMotion}
          avatarName={selectedAvatarForMotion.name}
          avatarId={selectedAvatarForMotion._id}
          isLoading={isAddingMotion}
        />
      )}

      {/* Avatar Lab Info Dialog */}
      <Dialog
        open={isAvatarLabInfoOpen}
        onClose={() => setIsAvatarLabInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon sx={{ color: THEME_COLORS.darkYellow }} />
            What is Avatar Lab?
          </Box>
          <IconButton
            onClick={() => setIsAvatarLabInfoOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Avatar Lab is your creative space for building custom AI avatars with
            consistent identity across different scenarios.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            When you create a new avatar, the system automatically generates an{" "}
            <strong>Avatar Group</strong> — a container that maintains your
            avatar's identity while allowing you to create multiple{" "}
            <strong>Looks</strong>.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Looks</strong> are variations of your avatar in different
            settings, outfits, or scenarios — the same person at home, in an
            office, at a park, or in any environment you can imagine. This
            ensures visual consistency while giving you creative flexibility.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            You can also enhance your avatars with:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>
              <Typography variant="body2">
                <strong>Motion</strong> — Add realistic movement and animations
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Sound Effects</strong> — Include ambient audio for more
                engaging videos
              </Typography>
            </li>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: THEME_COLORS.gray, fontStyle: "italic" }}
          >
            Start by creating a new avatar, and the system will handle the rest!
          </Typography>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ActorsDialog;
